import Axios from "axios";
import React, { useEffect, useState } from "react";
import useDebounce from "../useDebounce";
import { selectedLGAList, statesList } from "../../../services/dashboard-api";

const AddLocationModal = ({
  stateId,
  states,
  setStates,
  setLgaId,
  openModal,
  surveyData,
  setOpenModal,
  activeStep,
  stateLga,
  setStateLga,
  activeStateIds,
  setActiveStateIds,
  setNewSurveyData,
  handleNewSurveyData,
  locationModalAction,
  handleUpdatedEstimatedRespondent,
}) => {
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [selectedStateId, setSelectedStateId] = useState(0);
  const [oldStateId, setOldSatetId] = useState(stateId);
  const [oldLgas, setOldLgas] = useState([]);
  const [showLgaDropdown, setShowLgaDropdown] = useState(false);
  const [selectedLgas, setSelectedLgas] = useState([]); // Stores an array of LGA IDs (numbers)
  const [filteredSelectedLgas, setFilteredSelectedLgas] = useState([]);
  const [q, setQ] = useState("");

  let [startSearch, search] = useDebounce(q, 500);

  const handleSearchLga = (query) => {
    if (query) {
      let lgaList = stateLga?.filter((lga) => {
        let regex = new RegExp(query, "ig");
        return regex.test(lga.name);
      });
      setFilteredSelectedLgas(lgaList);
    } else {
      setFilteredSelectedLgas([]);
    }
  };

  const handleSelectedState = (e) => {
    const stateIdValue = e.target.value;
    const uniqueIds = new Set([...activeStateIds, stateIdValue]);
    setActiveStateIds(Array.from(uniqueIds));
    setSelectedLgas([]);
    setSelectedStateId(stateIdValue);
  };

  const handleSelectedLga = (lgaObject) => {
    const lgaIdExists = selectedLgas?.some((id) => id === lgaObject.id);

    if (lgaIdExists) {
      const newSelectedLgaIds = selectedLgas?.filter(
        (id) => id !== lgaObject.id
      );
      setSelectedLgas(newSelectedLgaIds);
    } else {
      setSelectedLgas([...selectedLgas, lgaObject.id]);
    }
  };

  const handleUpdateLocation = () => {
    // oldLgas and newLgas are arrays of numbers (LGA IDs)
    const oldLgaIdsToRemove = oldLgas;
    const newLgaIdsToAdd = selectedLgas;

    if (selectedStateId && selectedStateId !== "0" && oldStateId) {
      const newStateIds = surveyData?.stateOrRegion?.filter(
        (id) => id != oldStateId
      );

      // --- CRITICAL CORRECTION HERE ---
      // 1. Get existing lga_ids from surveyData, ensure they are strings.
      const existingSurveyLgaIds = surveyData?.lga_ids?.map(String) || [];

      // 2. Filter out the old LGAs, ensuring consistent type for comparison.
      // We convert oldLgaIdsToRemove (numbers) to strings for comparison with existingSurveyLgaIds (strings).
      const currentLgaIdsAfterRemoval = existingSurveyLgaIds.filter(
        (idString) => !oldLgaIdsToRemove.map(String).includes(idString)
      );

      // 3. Combine with newLgaIdsToAdd, ensuring they are also strings.
      // Use a Set to get unique IDs, ensuring they are strings.
      const uniqueLgaIds = new Set([
        ...currentLgaIdsAfterRemoval,
        ...newLgaIdsToAdd.map(String),
      ]);
      // --- END CRITICAL CORRECTION ---

      const uniqueStateIds = new Set([
        ...newStateIds,
        parseInt(selectedStateId),
      ]);

      setNewSurveyData({
        ...surveyData,
        stateOrRegion: Array.from(uniqueStateIds),
        lga_ids: Array.from(uniqueLgaIds), // This will be an array of strings like ["1", "4"]
      });
      setActiveStateIds([]);
      handleUpdatedEstimatedRespondent();
      setOpenModal(false);
    }
  };

  const handleDeleteLocation = () => {
    const idsToDelete = selectedLgas; // These are numbers
    const newStateIds = surveyData?.stateOrRegion?.filter(
      (id) => id != stateId
    );
    // --- CRITICAL CORRECTION HERE ---
    const existingSurveyLgaIds = surveyData?.lga_ids?.map(String) || [];
    const newLgaIds = existingSurveyLgaIds.filter(
      (idString) => !idsToDelete.map(String).includes(idString) // Convert idsToDelete (numbers) to strings for comparison
    );
    // --- END CRITICAL CORRECTION ---

    setNewSurveyData({
      ...surveyData,
      stateOrRegion: newStateIds,
      lga_ids: newLgaIds, // This will be an array of strings
    });
    setActiveStateIds([]);
    handleUpdatedEstimatedRespondent();
    setOpenModal(false);
  };

  const handleAddLocation = () => {
    const idsToAdd = selectedLgas; // These are numbers
    // --- CRITICAL CORRECTION HERE ---
    const existingSurveyLgaIds = surveyData?.lga_ids?.map(String) || [];
    const uniqueLgaIds = new Set([
      ...existingSurveyLgaIds,
      ...idsToAdd.map(String), // Convert to strings
    ]);
    // --- END CRITICAL CORRECTION ---

    const uniqueStateIds = new Set([
      ...surveyData?.stateOrRegion,
      ...activeStateIds,
    ]);

    setNewSurveyData({
      ...surveyData,
      stateOrRegion: Array.from(uniqueStateIds),
      lga_ids: Array.from(uniqueLgaIds), // This will be an array of strings
    });
    handleUpdatedEstimatedRespondent();
    setOpenModal(false);
  };

  useEffect(() => {
    if (startSearch) {
      handleSearchLga(search);
    } else if (q === "") {
      setFilteredSelectedLgas([]);
    }
  }, [startSearch, search, q, stateLga]);

  useEffect(() => {
    if (states.length === 0) {
      setIsLoadingStates(true);
      const getStatesList = async () => {
        try {
          const { data } = await statesList();
          const stateList = data?.data;
          console.log("Fetched stateList", stateList);
          setStates(stateList);
        } catch (error) {
          console.log("Error obtaining states", error.message);
        } finally {
          setIsLoadingStates(false);
        }
      };
      getStatesList();
    }
  }, [activeStep, states.length, setStates]);

  useEffect(() => {
    console.log("selectedStateId changed:", selectedStateId);

    if (selectedStateId && selectedStateId !== "0") {
      setIsLoadingStates(true);

      const getLGAList = async () => {
        try {
          const { data } = await selectedLGAList();
          const allLgaList = data?.data;

          const filteredLgasForState = allLgaList?.filter(
            (lga) => lga.state_id == parseInt(selectedStateId)
          );

          console.log(
            "Filtered lgaList for state",
            selectedStateId,
            filteredLgasForState
          );
          setStateLga(filteredLgasForState);
        } catch (error) {
          console.log("Error obtaining LGAS", error.message);
          setStateLga([]);
        } finally {
          setIsLoadingStates(false);
        }
      };
      getLGAList();
    } else {
      setStateLga([]);
      setFilteredSelectedLgas([]);
      setQ("");
    }
  }, [selectedStateId, setStateLga]);

  useEffect(() => {
    if (locationModalAction === "update" && states.length && stateId) {
      const stateToUpdate = states.find((_state) => _state?.id == stateId);
      if (stateToUpdate) {
        setSelectedStateId(stateToUpdate.id);

        setIsLoadingStates(true);
        const getLGAsForUpdate = async () => {
          try {
            const { data } = await selectedLGAList();
            const allLgas = data?.data;

            const lgasForCurrentState = allLgas?.filter(
              (lga) => lga.state_id == stateToUpdate.id
            );
            setStateLga(lgasForCurrentState);

            // Populate selectedLgas with only the IDs (as numbers)
            // Compare string IDs from surveyData with numeric IDs from `lga` objects
            const initialSelectedLgaIds = lgasForCurrentState
              ?.filter((lga) => surveyData?.lga_ids?.includes(String(lga?.id)))
              .map((lga) => lga.id);

            setSelectedLgas(initialSelectedLgaIds);
            setOldLgas(initialSelectedLgaIds);
          } catch (error) {
            console.log("Error obtaining LGAs for update", error.message);
            setStateLga([]);
            setSelectedLgas([]);
            setOldLgas([]);
          } finally {
            setIsLoadingStates(false);
          }
        };
        getLGAsForUpdate();
      }
    }
  }, [
    locationModalAction,
    states,
    stateId,
    surveyData?.lga_ids,
    setSelectedStateId,
    setStateLga,
    setSelectedLgas,
    setOldLgas,
  ]);

  console.log("selectedLgas:", selectedLgas.map(String));
  return (
    <div
      data-w-id="fc7f959f-74ff-b010-43f8-3e0a4325a509"
      className="modal"
      style={{
        display: openModal ? "flex" : "none",
        opacity: openModal ? 1 : 0,
      }}
    >
      <div className="modal-container">
        <h2>Add a Location</h2>
        <div data-w-id="fc7f959f-74ff-b010-43f8-3e0a4325a50d">
          <svg
            onClick={() => setOpenModal(false)}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="feather feather-x icon close"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </div>
        <div
          className="modal-card-location-filter"
          style={{
            opacity: 1,
            transform:
              "translate3d(0px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)",
            transformStyle: "preserve-3d",
          }}
        >
          <div className="modal-header"></div>
          <div className="modal-body" style={{ minHeight: "280px" }}>
            <div className="no-margin w-form">
              <label
                htmlFor="Industry-or-Interet-2"
                className="field-label-height"
              >
                State
              </label>
              <select
                id="Industry-or-Interet-2"
                name="Industry-or-Interet-2"
                data-name="Industry Or Interet 2"
                className="input large w-select"
                value={selectedStateId}
                onChange={handleSelectedState}
              >
                <option value="0">
                  {" "}
                  {isLoadingStates
                    ? "Loading States..."
                    : "Select State..."}{" "}
                </option>
                {console.log("states", states)}
                {states.map((state) => {
                  return (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  );
                })}
              </select>
              &nbsp;
              <label htmlFor="LGA-Dropdown">Local Government Area</label>
              <div
                style={{ minHeight: "50px" }}
                data-hover="false"
                data-delay="0"
                id="w-node-f5104c23-d019-0d24-519f-928b04075c47-2a3b9cb4"
                className="input large nudge-left w-dropdown"
              >
                <div
                  className="dropdown-toggle-2 w-dropdown-toggle"
                  id="w-dropdown-toggle-0"
                  aria-controls="w-dropdown-list-0"
                  aria-haspopup="menu"
                  aria-expanded={showLgaDropdown}
                  role="button"
                  tabIndex="0"
                  onClick={() => setShowLgaDropdown(!showLgaDropdown)}
                >
                  <div
                    className="icon-3 w-icon-dropdown-toggle"
                    aria-hidden="true"
                  ></div>
                  <div
                    className="dropdown-toggle-2"
                    style={{ width: "470px", overflow: "scroll" }}
                  >
                    {/* Display selected LGA names here */}
                    {selectedLgas?.length > 0 ? (
                      selectedLgas.map((selectedLgaId) => {
                        const lgaObject = stateLga.find(
                          (lga) => lga.id === selectedLgaId
                        );
                        return lgaObject ? (
                          <a
                            key={lgaObject.id}
                            href="#"
                            className="button with-object tag location dropdown w-button"
                            onClick={(e) => {
                              e.preventDefault();
                              handleSelectedLga(lgaObject);
                            }}
                          >
                            {lgaObject.name}
                          </a>
                        ) : null;
                      })
                    ) : (
                      <div style={{ padding: "8px", color: "#888" }}>
                        {selectedStateId === "0"
                          ? "Please select a state"
                          : "No LGAs selected"}
                      </div>
                    )}
                  </div>
                </div>
                {showLgaDropdown ? (
                  <nav
                    className="dropdown-list-input w-dropdown-list w--open"
                    id="w-dropdown-list-0"
                    aria-labelledby="w-dropdown-toggle-0"
                    style={{
                      height: "202px",
                      overflowY: "auto",
                    }}
                  >
                    <div className="search survey">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-search icon"
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <input
                        className="input survey-search w-input"
                        maxLength="256"
                        name="lga-search"
                        data-name="LGA Search"
                        placeholder="Search LGAs"
                        type="text"
                        id="lga-search-input"
                        value={q}
                        onChange={(e) => {
                          setQ(e.target.value);
                        }}
                      />
                    </div>
                    <div
                      className="column"
                      style={{
                        marginLeft: "-12px",
                        maxHeight: "120px",
                        overflowY: "auto",
                      }}
                    >
                      {isLoadingStates ? (
                        <div
                          style={{
                            padding: "10px",
                            textAlign: "center",
                            color: "#555",
                          }}
                        >
                          Loading LGAs...
                        </div>
                      ) : q &&
                        filteredSelectedLgas.length === 0 &&
                        stateLga.length > 0 ? (
                        <div
                          style={{
                            padding: "10px",
                            textAlign: "center",
                            color: "#555",
                          }}
                        >
                          No LGAs found for "{q}"
                        </div>
                      ) : (q && filteredSelectedLgas.length > 0
                          ? filteredSelectedLgas
                          : stateLga
                        )?.length > 0 ? (
                        (q && filteredSelectedLgas.length > 0
                          ? filteredSelectedLgas
                          : stateLga
                        )?.map((lga) => {
                          const inSelectedLga = selectedLgas?.includes(lga.id);
                          return (
                            <label
                              key={lga.id}
                              value={lga.id}
                              className="w-checkbox checkbox-field-2 move-down dropdown"
                            >
                              <input
                                type="checkbox"
                                id={`checkbox-${lga.id}`}
                                name={`checkbox-${lga.id}`}
                                data-name={`Checkbox ${lga.id}`}
                                className="w-checkbox-input checkbox"
                                checked={inSelectedLga}
                                onChange={() => handleSelectedLga(lga)}
                              />
                              <span
                                className="checkbox-label dropdown w-form-label"
                                htmlFor={`checkbox-${lga.id}`}
                              >
                                {lga.name}
                              </span>
                            </label>
                          );
                        })
                      ) : (
                        <div
                          style={{
                            padding: "10px",
                            textAlign: "center",
                            color: "#555",
                          }}
                        >
                          {selectedStateId === "0"
                            ? "Please select a state to view LGAs."
                            : "No LGAs available for this state."}
                        </div>
                      )}
                    </div>
                  </nav>
                ) : (
                  ""
                )}
              </div>
              <div className="row col-16">
                <input
                  onClick={() => handleDeleteLocation()}
                  type="button"
                  value="Delete"
                  data-wait="Please wait..."
                  data-w-id="fc7f959f-74ff-b010-43f8-3e0a4325a519"
                  className="button danger wide w-button"
                />
                &nbsp; &nbsp;
                <input
                  onClick={() => {
                    if (locationModalAction === "new") {
                      handleAddLocation();
                    } else {
                      handleUpdateLocation();
                    }
                  }}
                  type="button"
                  value="Save Changes"
                  data-wait="Please wait..."
                  data-w-id="fc7f959f-74ff-b010-43f8-3e0a4325a51a"
                  className="button primary wide w-button"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLocationModal;
